import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { deleteClaimAttachment, uploadClaimAttachment } from 'api/Claim';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import FileUploader from 'components/shared/FileUploader/FileUploader';
import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';

const Attachments = ({ claim, attachments = [], setClaim }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const claim_id = claim?.id;

  const updateClaim = (updatedAttachments) => {
    setClaim((prev) => ({ ...prev, attachments: updatedAttachments, claim_md_synced: false }));
    queryClient.setQueryData(['claim', claim_id.toString()], (prev) => ({
      ...prev,
      claim: {
        ...prev.claim,
        attachments: updatedAttachments,
        claim_md_synced: false
      }
    }));
  };

  const { mutate: uploadAttachment } = useCustomMutation({
    mutationFn: (params) => uploadClaimAttachment(navigate, params),
    disableAlert: true,
    onSuccess: (params) => {
      updateClaim([...attachments, params.data]);
      queryClient.refetchQueries({ queryKey: ['activities', 'claim', Number(claim_id)] });
    }
  });

  const { mutate: deleteAttachment } = useCustomMutation({
    mutationFn: (params) => deleteClaimAttachment(navigate, params),
    disableAlert: true,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['activities', 'claim', Number(claim_id)] });
    }
  });

  const saveImage = async (data) => {
    await uploadAttachment({ claim_id, handler_id: data.handle, name: data.name });
  };

  const handleDelete = async (handle) => {
    const attachment = attachments.find((_attachment) => _attachment.handler_id === handle);
    await deleteAttachment({ claim_id, id: attachment.id });
    const updatedAttachments = attachments.filter((a) => a.id !== attachment.id);
    updateClaim(updatedAttachments);
  };

  const handlers = useMemo(
    () => attachments.map((attachment) => attachment.handler_id),
    [attachments]
  );

  if (!claim?.payer?.attachments || !claim_id)
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-5 px-3 py-0">
        <Icon icon="illustration-permissions" />
        <Typography color="neutral-500" size="large" className="text-center">
          The selected payer does not accept attachments.
        </Typography>
      </div>
    );

  return (
    <div className="flex flex-1 flex-col gap-5 px-3 py-0">
      <FileUploader
        handlers={handlers}
        onUpload={saveImage}
        onDelete={handleDelete}
        isMultiple
        maxFiles={10}
      />
    </div>
  );
};

export default Attachments;
